/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import { EuiBadge, EuiHealth, useEuiTheme } from '@elastic/eui'

import type { LineItem } from '@modules/billing-api/customTypes'
import { isActiveItem, isExpiredItem, isFutureItem } from '@modules/billing-lib/utils'
import { FormattedMessage } from '@modules/i18n'

interface Props {
  lineItem: LineItem
  type: 'badge' | 'health'
  color?: string
}

const LineItemStatus = ({ lineItem, type = 'health', color }: Props) => {
  const theme = useEuiTheme()
  const RenderComponent = { badge: EuiBadge, health: EuiHealth }[type]

  if (isActiveItem(lineItem)) {
    return (
      <RenderComponent color={color || theme.euiTheme.colors.success}>
        <FormattedMessage id='billing-details-summary.active-label' defaultMessage='Active' />
      </RenderComponent>
    )
  }

  if (isExpiredItem(lineItem)) {
    if (lineItem.end_type === 'depletion') {
      return (
        <RenderComponent color={color || theme.euiTheme.colors.mediumShade}>
          <FormattedMessage id='billing-details-summary.depleted-label' defaultMessage='Depleted' />
        </RenderComponent>
      )
    }

    return (
      <RenderComponent color={color || theme.euiTheme.colors.mediumShade}>
        <FormattedMessage id='billing-details-summary.expired-label' defaultMessage='Expired' />
      </RenderComponent>
    )
  }

  if (isFutureItem(lineItem)) {
    return (
      <RenderComponent color={color || theme.euiTheme.colors.warning}>
        <FormattedMessage id='billing-details-summary.future-label' defaultMessage='Future' />
      </RenderComponent>
    )
  }

  return null
}

export default LineItemStatus
